import React, { useState, useEffect, useContext } from "react";
import { keyBy, cloneDeep } from "lodash";
import * as Tabs from "@radix-ui/react-tabs";
import { gql, useMutation, useQuery } from "urql";
import { Link, useLocation, useParams, useSearchParams } from "wouter";

import { Button } from "../../../components/src/Button";
import { Panel } from "../../../components/src/common/Panel";
import { emptyObjectId } from "../../../common/src";
import { usePrevious } from "../../../components/src/common/Utils";
import { DateForm } from "../../../components/src/dateForm";

import { MiniTransactionForm, miniTransactionTypes, NewMiniTransactionButton } from "./MiniTransactionForm";
import { UpdateTransactionsStatusGrid } from "../components/UpdateTransactionStatusGrid";
import { ITransactionDataRow } from "../components/TransactionsGrid";
import { useQueryState } from "../../../components/src/use-query-state";
import { serializeSwedenDate } from "../../../components/src/dateFormater";
import { PartyType, UpdateTransactionInput } from "../types.generated";
import { ClientContextSelector } from "../contexts/ClientContextSelector";
import { ClientContext, IClient } from "../../../components/src/contexts/ClientContext";
import { RotateCcw } from "lucide-react";
import { ResponsiveTabsList } from "../../../components/src/ResponsiveTabsList";

const GET_CASH_BALANCE = gql`
    query cashBalances($instrumentIds: [GraphQLObjectId!]!, $viewFromDate: GraphQLDateString!) {
        cashBalances(instrumentIds: $instrumentIds, viewFromDate: $viewFromDate) {
            instrumentId
            instrument {
                name
                currency
            }
            balanceItems {
                transactionId
                transaction {
                    status
                    type
                    updateUserInfo {
                        name
                    }
                    updateTimestamp
                }
                tradeDate
                valueDate
                amount
                balance
            }
        }
    }
`;

const GET_CLIENTS = gql`
    query {
        clients: parties(filter: { typeIn: [Fund, Client] }) {
            _id
            name
            instruments {
                name
                _id
            }
        }
    }
`;

const UPDATE_TRANSACTIONS = gql`
    mutation updateTransactions($input: [UpdateTransactionInput!]!) {
        updateTransactions(input: $input) {
            _id
        }
    }
`;

const defaultViewFromDateString = () => {
    return serializeSwedenDate(new Date(new Date().setDate(new Date().getDate() - 15)));
};

const transactionGroups = ["Not settled", "Settled"];

const groupByStatus = (transactions, transactionGroups) => {
    const result = [];
    transactionGroups.forEach((status) => {
        result[status] = [];
    });

    transactions.forEach((item) => {
        if (item.transaction.status === "Settled") {
            item.statusGroup = item.transaction.status;
        } else {
            item.statusGroup = "Not settled";
        }
        if (transactionGroups.includes(item.statusGroup)) {
            result[item.statusGroup].push(item);
        }
    });

    return result;
};

export const PositionCashBalancePage = (): React.ReactElement => {
    const [queryArgs] = useSearchParams();
    const [, navigate] = useLocation();

    const { client } = useContext(ClientContext);
    const previousClient: IClient = usePrevious(client);
    const [viewFromDate] = useQueryState("viewFromDate", defaultViewFromDateString());
    const [tab, setTab] = useState(null);
    const [instrumentIds, setInstrumentIds] = useState([emptyObjectId]);

    const [{ fetching: loadingClients, error: errorClients, data: dataClients }] = useQuery({
        query: GET_CLIENTS,
        requestPolicy: "cache-and-network"
    });

    const [{ error: errorUpdate }, updateTransactionsStatus] = useMutation(UPDATE_TRANSACTIONS);

    const params = useParams<{ id: string }>();
    let id = params.id;

    let type = miniTransactionTypes[0];

    if (id && id.startsWith("new")) {
        type = miniTransactionTypes.find((d) => d.toLowerCase() === id.substring(3, id.length));
        id = "new";
    }

    const [{ fetching: loadingCashBalance, error: errorCashBalance, data: dataCashBalances }, refetch] = useQuery({
        query: GET_CASH_BALANCE,
        variables: { viewFromDate: viewFromDate || defaultViewFromDateString(), instrumentIds },
        requestPolicy: "network-only"
    });

    useEffect(() => {
        if (dataClients) {
            if (client) {
                if (
                    (client && instrumentIds.length === 1 && instrumentIds[0] === emptyObjectId) ||
                    (client && previousClient && client._id !== previousClient._id)
                ) {
                    for (let i = 0; i < dataClients.clients.length; i++) {
                        const clientData = dataClients.clients[i];
                        if (client._id === clientData._id) {
                            setInstrumentIds(clientData.instruments.map((instrument) => instrument._id));
                            break;
                        }
                    }
                }
            }
        }

        /*if (client && client._id === emptyObjectId && Object.keys(queryArgs).length) {
            navigate("/reconcile/cashbalance/" + client.dashName + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""));
        }*/
    }, [dataClients, client, instrumentIds, previousClient, queryArgs, navigate]);

    if (loadingClients) return <p>Loading clients</p>;
    if (errorClients) return <p>errorClients: {JSON.stringify(errorClients, null, 2)}</p>;
    if (errorUpdate) return <p>error: {JSON.stringify(errorUpdate, null, 2)}</p>;
    if (errorCashBalance) return <p>errorCashBalance: {JSON.stringify(errorCashBalance, null, 2)}</p>;

    if (!dataCashBalances) {
        return <div>Loading cash balance</div>;
    }

    const cashBalancesClone = cloneDeep(dataCashBalances.cashBalances);

    const groupedByInstrumentId = keyBy(cashBalancesClone, "instrumentId");
    const tabs = {};
    Object.keys(groupedByInstrumentId).forEach((instrumentId) => {
        tabs[instrumentId] = groupedByInstrumentId[instrumentId].instrument.name;
    });

    const tabNames = [];
    for (const key in tabs) {
        tabNames.push(tabs[key]);
    }
    const tabNamesSorted = tabNames.sort();
    const tabsSortedByName = {};
    tabNamesSorted.forEach((instrumentName) => {
        for (const tabKey in tabs) {
            if (tabs[tabKey] === instrumentName) {
                tabsSortedByName[tabKey] = instrumentName;
            }
        }
    });

    let activeTab = null;
    if (tab && tabsSortedByName[tab]) {
        activeTab = tab;
    } else if (Object.keys(tabsSortedByName).length > 0) {
        activeTab = Object.keys(tabsSortedByName)[0];
    }

    let groups = {};
    if (Object.keys(groupedByInstrumentId).length > 0 && groupedByInstrumentId[activeTab]) {
        groups = groupByStatus(groupedByInstrumentId[activeTab].balanceItems, transactionGroups);
    }

    const handleStatusUpdate = async (input: UpdateTransactionInput[]) => {
        await updateTransactionsStatus({ input: input })
            .then((result) => {
                if (result.error) {
                    console.error(result.error.toString());
                } else {
                    refetch();
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const toTransactionItems = (items: any, instrumentId, instrumentName, currency): ITransactionDataRow[] => {
        if (items) {
            return items.map((item: any) => {
                return {
                    _id: item.transactionId,
                    amount: item.amount,
                    currency: currency,
                    instrumentId: instrumentId,
                    instrumentName: instrumentName,
                    tradeDate: item.tradeDate,
                    valueDate: item.valueDate,
                    type: item.transaction.type,
                    //types: item.types,
                    balance: item.balance,
                    description: item.description,
                    status: item.transaction.status,
                    updatedByUserName: item.transaction.updateUserInfo ? item.transaction.updateUserInfo.name : ""
                };
            });
        } else {
            return [];
        }
    };

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full md:w-4/12 px-2 mb-4">
                    <ClientContextSelector typeIn={[PartyType.Fund]} />
                </div>
            </div>

            {client && client._id !== emptyObjectId ? (
                <div>
                    <div className="flex flex-wrap -mx-2 mt-4">
                        <div className="w-full md:w-4/12 px-2 mb-4">
                            <DateForm defaultDateString={viewFromDate} dateName={"viewFromDate"} className="width-fixed"></DateForm>
                        </div>
                        <div className="w-full md:w-8/12 px-2 mb-4 text-right">
                            <NewMiniTransactionButton page={`reconcile/cashbalance/${client.dashName}`} />
                        </div>
                    </div>
                    <div className="flex flex-wrap -mx-2 mb-2 mt-2">
                        <div className="w-full md:w-4/12 px-2 mb-4">
                            <Button
                                variant={loadingCashBalance ? "secondary" : "primary"}
                                disabled={loadingCashBalance}
                                onClick={() => {
                                    refetch();
                                }}
                            >
                                <RotateCcw size={20} />
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-wrap -mx-2">
                        <div className={"w-full px-2 mb-4"}>
                            {activeTab && client && client._id ? (
                                <Tabs.Root value={activeTab} onValueChange={setTab} className="w-full">
                                    <ResponsiveTabsList value={activeTab} onValueChange={setTab}>
                                        {Object.keys(tabsSortedByName).map((instrumentId) => (
                                            <Tabs.Trigger
                                                key={instrumentId}
                                                value={instrumentId}
                                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                            >
                                                {tabsSortedByName[instrumentId]}
                                            </Tabs.Trigger>
                                        ))}
                                    </ResponsiveTabsList>
                                    {Object.keys(tabsSortedByName).map((instrumentId) => {
                                        const instrumentName = groupedByInstrumentId[instrumentId].instrument.name;
                                        const instrumentCurrency = groupedByInstrumentId[instrumentId].instrument.currency;

                                        return (
                                            <Tabs.Content key={instrumentId} value={instrumentId} className="mt-4">
                                                <Link to={"/parties/" + client._id + "/instruments/" + instrumentId}>Instrument</Link>
                                                <br />
                                                {transactionGroups.map((statusGroup) => (
                                                    <div key={statusGroup} className="mt-4 mb-4">
                                                        <h2>{statusGroup}</h2>
                                                        {groups[statusGroup] ? (
                                                            <UpdateTransactionsStatusGrid
                                                                key={"sg" + groups[statusGroup].length}
                                                                items={toTransactionItems(
                                                                    groups[statusGroup],
                                                                    instrumentId,
                                                                    instrumentName,
                                                                    instrumentCurrency
                                                                )}
                                                                onUpdateStatus={handleStatusUpdate}
                                                                visibleColumns={[
                                                                    "id",
                                                                    "instrument",
                                                                    "currency",
                                                                    "status",
                                                                    "tradeDate",
                                                                    "valueDate",
                                                                    "type",
                                                                    // "types",
                                                                    "amount",
                                                                    "balance",
                                                                    "description",
                                                                    "updatedBy"
                                                                ]}
                                                            />
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </Tabs.Content>
                                        );
                                    })}
                                </Tabs.Root>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
            <Panel
                isOpen={id != null}
                onDismiss={() => {
                    if (queryArgs.toString()) {
                        navigate(`/reconcile/cashbalance/${client.dashName}?${queryArgs.toString()}`);
                    } else {
                        navigate(`/reconcile/cashbalance/${client.dashName}`);
                    }
                }}
            >
                {id ? <MiniTransactionForm id={id === "new" ? null : id} type={id === "new" ? type : null} showHeader /> : null}
            </Panel>
        </div>
    );
};
